import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import Button from './ui/Button';
import { Check, Sparkles, ExternalLink } from 'lucide-react';
import SizeChartModal from './SizeChartModal';
import styled from 'styled-components';

const heroImage = 'https://trendykool.com/wp-content/uploads/2024/07/kf-Sd3e28b06fe374905a4342f8e7eafee64c-Luxury-Sky-Blue-Off-the-Shoulder-Quinceanera-Appliques-Lace-Beads-2024-Ball-Gown-Floor-Length-Sweep.webp';
const productUrl = 'https://trendykool.com/product/sky-blue-quinceanera-dresses/';

const Section = styled.section`
  padding: ${props => props.theme.spacing['4xl']} 0;
  background: linear-gradient(to bottom, ${props => props.theme.colors.primary.main}0D, ${props => props.theme.colors.background.main});
  position: relative;
  overflow: hidden;
  id: featured;
  
  @media (min-width: ${props => props.theme.breakpoints.md}) {
    padding: 8rem 0;
  }
`;

const DecorativeElement = styled.div`
  position: absolute;
  width: 18rem;
  height: 18rem;
  border-radius: 50%;
  filter: blur(60px);
  pointer-events: none;
  
  &:nth-child(1) {
    top: 5rem;
    left: 0;
    background: ${props => props.theme.colors.primary.main}1A;
  }
  
  &:nth-child(2) {
    bottom: 5rem;
    right: 0;
    width: 24rem;
    height: 24rem;
    background: ${props => props.theme.colors.accent.main}1A;
  }
`;

const Container = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing['2xl']};
  position: relative;
  z-index: 10;
`;

const ContentWrapper = styled.div`
  max-width: 80rem;
  margin: 0 auto;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
`;

const Subtitle = styled.span`
  color: ${props => props.theme.colors.accent.gold};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  letter-spacing: 0.1em;
  text-transform: uppercase;
  font-size: ${props => props.theme.typography.fontSize.sm};
`;

const Title = styled.h2`
  font-family: ${props => props.theme.typography.fontFamily.display};
  font-size: ${props => props.theme.typography.fontSize['4xl']};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.text.primary};
  margin-top: ${props => props.theme.spacing.lg};
  margin-bottom: ${props => props.theme.spacing['2xl']};
  
  @media (min-width: ${props => props.theme.breakpoints.md}) {
    font-size: ${props => props.theme.typography.fontSize['5xl']};
  }
  
  @media (min-width: ${props => props.theme.breakpoints.lg}) {
    font-size: ${props => props.theme.typography.fontSize['6xl']};
  }
  
  @media (min-width: ${props => props.theme.breakpoints.xl}) {
    font-size: ${props => props.theme.typography.fontSize['7xl']};
  }
  
  @media (min-width: ${props => props.theme.breakpoints['2xl']}) {
    font-size: ${props => props.theme.typography.fontSize['8xl']};
  }
`;

const Description = styled.p`
  color: ${props => props.theme.colors.text.muted};
  font-size: ${props => props.theme.typography.fontSize.lg};
  max-width: 42rem;
  margin: 0 auto;
  line-height: 1.7;
  
  @media (min-width: ${props => props.theme.breakpoints.md}) {
    font-size: ${props => props.theme.typography.fontSize.xl};
    max-width: 48rem;
  }
  
  @media (min-width: ${props => props.theme.breakpoints.lg}) {
    font-size: ${props => props.theme.typography.fontSize['2xl']};
    max-width: 56rem;
  }
  
  @media (min-width: ${props => props.theme.breakpoints.xl}) {
    font-size: ${props => props.theme.typography.fontSize['3xl']};
    max-width: 64rem;
  }
`;

const ProductGrid = styled.div`
  display: grid;
  gap: 3rem;
  
  @media (min-width: ${props => props.theme.breakpoints.lg}) {
    grid-template-columns: repeat(2, 1fr);
    gap: 5rem;
    align-items: center;
  }
`;

const ImageWrapper = styled.div`
  position: relative;
  
  &:hover .image-glow {
    transform: scale(1.05);
  }
`;

const ImageGlow = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(to bottom right, ${props => props.theme.colors.primary.main}33, ${props => props.theme.colors.accent.main}33);
  border-radius: ${props => props.theme.borderRadius['3xl']};
  filter: blur(32px);
  transform: scale(1);
  transition: ${props => props.theme.transitions.slow};
`;

const ImageContainer = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: ${props => props.theme.borderRadius['3xl']};
  box-shadow: ${props => props.theme.shadows.xl};
  
  img {
    width: 100%;
    height: auto;
    object-fit: cover;
    transform: scale(1);
    transition: transform 0.7s ease;
  }
  
  ${ImageWrapper}:hover & img {
    transform: scale(1.05);
  }
`;

const PriceBadge = styled.div`
  position: absolute;
  top: ${props => props.theme.spacing['2xl']};
  right: ${props => props.theme.spacing['2xl']};
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(8px);
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing['2xl']};
  border-radius: ${props => props.theme.borderRadius.full};
  box-shadow: ${props => props.theme.shadows.lg};
  
  span:first-child {
    color: ${props => props.theme.colors.text.muted};
    font-size: ${props => props.theme.typography.fontSize.sm};
  }
  
  span:last-child {
    font-family: ${props => props.theme.typography.fontFamily.display};
    font-size: ${props => props.theme.typography.fontSize['2xl']};
    font-weight: ${props => props.theme.typography.fontWeight.bold};
    color: ${props => props.theme.colors.primary.main};
    margin-left: ${props => props.theme.spacing.sm};
  }
`;

const ProductDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing['2xl']};
`;

const FeaturesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing['2xl']};
`;

const FeatureItem = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.lg};
`;

const FeatureIcon = styled.div`
  flex-shrink: 0;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  background-color: ${props => props.theme.colors.primary.main}1A;
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    width: 1.25rem;
    height: 1.25rem;
    color: ${props => props.theme.colors.primary.main};
  }
`;

const FeatureContent = styled.div`
  h4 {
    font-weight: ${props => props.theme.typography.fontWeight.semibold};
    color: ${props => props.theme.colors.text.primary};
    margin-bottom: ${props => props.theme.spacing.xs};
  }
  
  p {
    color: ${props => props.theme.colors.text.muted};
    font-size: ${props => props.theme.typography.fontSize.sm};
    line-height: 1.7;
    margin: 0;
  }
`;

const CustomizableNote = styled.div`
  background-color: ${props => props.theme.colors.accent.main}4D;
  border: 1px solid ${props => props.theme.colors.accent.main}33;
  border-radius: ${props => props.theme.borderRadius['2xl']};
  padding: ${props => props.theme.spacing['2xl']};
  display: flex;
  align-items: flex-start;
  gap: ${props => props.theme.spacing.md};
  
  svg {
    width: 1.5rem;
    height: 1.5rem;
    color: ${props => props.theme.colors.accent.gold};
    flex-shrink: 0;
    margin-top: 0.125rem;
  }
  
  p {
    color: ${props => props.theme.colors.text.primary};
    font-weight: ${props => props.theme.typography.fontWeight.medium};
    margin: 0;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.lg};
  
  @media (min-width: ${props => props.theme.breakpoints.sm}) {
    flex-direction: row;
  }
`;

const FeaturedProductSection = () => {
  const { t } = useLanguage();

  const features = [
    {
      title: t('featured.feature1.title'),
      description: t('featured.feature1.desc'),
    },
    {
      title: t('featured.feature2.title'),
      description: t('featured.feature2.desc'),
    },
    {
      title: t('featured.feature3.title'),
      description: t('featured.feature3.desc'),
    },
    {
      title: t('featured.feature4.title'),
      description: t('featured.feature4.desc'),
    },
  ];

  return (
    <Section id="featured">
      <DecorativeElement />
      <DecorativeElement />
      <Container>
        <ContentWrapper>
          <SectionHeader>
            <Subtitle>{t('featured.subtitle')}</Subtitle>
            <Title>{t('featured.title')}</Title>
            <Description>{t('featured.description')}</Description>
          </SectionHeader>

          <ProductGrid>
            <ImageWrapper>
              <ImageGlow className="image-glow" />
              <ImageContainer>
                <img
                  src={heroImage}
                  alt="Luxury Sky Blue Quinceañera Dress"
                />
                <PriceBadge>
                  <span>{t('featured.price')}</span>
                  <span>$1,128.15</span>
                </PriceBadge>
              </ImageContainer>
            </ImageWrapper>

            <ProductDetails>
              <FeaturesList>
                {features.map((feature, index) => (
                  <FeatureItem key={index}>
                    <FeatureIcon>
                      <Check />
                    </FeatureIcon>
                    <FeatureContent>
                      <h4>{feature.title}</h4>
                      <p>{feature.description}</p>
                    </FeatureContent>
                  </FeatureItem>
                ))}
              </FeaturesList>

              <CustomizableNote>
                <Sparkles />
                <p>{t('featured.customizable')}</p>
              </CustomizableNote>

              <ButtonGroup>
                <a href={productUrl} target="_blank" rel="noopener noreferrer" style={{ flex: 1 }}>
                  <Button variant="cta" size="xl" style={{ width: '100%' }}>
                    <span>{t('featured.cta')}</span>
                    <ExternalLink style={{ width: '1.25rem', height: '1.25rem' }} />
                  </Button>
                </a>
                <SizeChartModal />
              </ButtonGroup>
            </ProductDetails>
          </ProductGrid>
        </ContentWrapper>
      </Container>
    </Section>
  );
};

export default FeaturedProductSection;
